<template>
  <div class="qr-login-container">
    <div v-if="loading" class="qr-loading">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <p>生成小程序码中...</p>
    </div>

    <div v-else-if="error" class="qr-error">
      <i class="pi pi-exclamation-circle" style="font-size: 2rem"></i>
      <p>{{ error }}</p>
      <Button
        label="重新生成"
        icon="pi pi-refresh"
        class="retry-button"
        @click="generateQRCode"
      />
    </div>

    <div v-else-if="qrCode" class="qr-content">
      <div class="qr-code-wrapper">
        <img :src="qrCode" alt="小程序码" class="qr-code-image" />
        <div v-if="status === 'expired'" class="qr-expired-overlay">
          <div class="expired-content">
            <i class="pi pi-clock" style="font-size: 3rem"></i>
            <p>二维码已过期</p>
            <Button
              label="刷新"
              icon="pi pi-refresh"
              class="refresh-button"
              @click="generateQRCode"
            />
          </div>
        </div>
        <div v-else-if="status === 'scanned'" class="qr-scanned-overlay">
          <div class="scanned-content">
            <i
              class="pi pi-check-circle"
              style="font-size: 3rem; color: #27ae60"
            ></i>
            <p>已扫码</p>
          </div>
        </div>
      </div>

      <!-- 已扫码状态：显示用户信息 -->
      <div v-if="status === 'scanned' && userPreview" class="user-preview">
        <div class="user-preview-header">
          <img
            v-if="userPreview.avatar_url"
            :src="userPreview.avatar_url"
            alt="用户头像"
            class="user-avatar"
          />
          <div v-else class="user-avatar-placeholder">
            <i class="pi pi-user" style="font-size: 2rem"></i>
          </div>
          <div class="user-info">
            <div class="user-name">{{ userPreview.username || '用户' }}</div>
            <div v-if="userPreview.phone" class="user-phone">
              ****{{ userPreview.phone }}
            </div>
          </div>
        </div>
        <div class="waiting-confirm">
          <i class="pi pi-spin pi-spinner"></i>
          <span>等待小程序确认登录...</span>
        </div>
      </div>

      <!-- 未扫码状态：显示使用说明 -->
      <div v-else class="qr-instructions">
        <ol>
          <li>打开微信扫描小程序码</li>
          <li>在小程序中确认登录</li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios'
import Button from 'primevue/button'
import { onMounted, onUnmounted, ref } from 'vue'

import { FASTAPI_CONFIG } from '@/config/fastapi'
import { useAuthStore } from '@/stores/authStore'

// 显式定义组件名称
defineOptions({
  name: 'QRLoginForm'
})

// 创建API客户端
const api = axios.create({
  baseURL: FASTAPI_CONFIG.BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
})

interface Props {
  onSuccess?: () => void
}

const props = withDefaults(defineProps<Props>(), {})

const authStore = useAuthStore()

// State
const loading = ref(false)
const error = ref<string | null>(null)
const qrCode = ref<string | null>(null)
const ticket = ref<string | null>(null)
const status = ref<'pending' | 'scanned' | 'expired' | 'confirmed'>('pending')
const countdown = ref(300) // 5分钟倒计时
const userPreview = ref<any>(null) // 用户预览信息

let pollingInterval: ReturnType<typeof setInterval> | null = null
let countdownInterval: ReturnType<typeof setInterval> | null = null
let pollingInProgress = false

// 生成二维码
const generateQRCode = async () => {
  loading.value = true
  error.value = null
  status.value = 'pending'
  countdown.value = 300

  try {
    const response = await api.post('/api/v1/auth/qr-login/generate', {
      page: 'pages/login/scanLogin'
    })

    if (response.data.success) {
      qrCode.value = response.data.qr_code
      ticket.value = response.data.ticket
      countdown.value = response.data.expires_in || 300

      // 开始轮询
      startPolling()
      // 开始倒计时
      startCountdown()
    } else {
      error.value = response.data.message || '生成小程序码失败'
    }
  } catch (err: any) {
    console.error('生成小程序码失败:', err)
    error.value = err.response?.data?.detail || '生成小程序码失败，请重试'
  } finally {
    loading.value = false
  }
}

// 开始轮询检查登录状态
const startPolling = () => {
  // 清除之前的轮询
  if (pollingInterval) {
    clearInterval(pollingInterval)
  }

  // 每2秒轮询一次
  pollingInterval = setInterval(async () => {
    if (!ticket.value || pollingInProgress) return
    pollingInProgress = true

    try {
      const response = await api.post('/api/v1/auth/qr-login/check', {
        ticket: ticket.value
      })

      if (response.data.status === 'scanned') {
        // 已扫码，显示用户预览信息
        status.value = 'scanned'
        userPreview.value = response.data.user_preview
        console.log('二维码已扫码，等待确认', userPreview.value)
      } else if (response.data.status === 'confirmed') {
        // 登录成功
        status.value = 'confirmed'
        stopPolling()
        stopCountdown()

        // 更新认证状态
        if (response.data.access_token) {
          // 使用 setAuthData 方法保存登录状态（会自动触发登录成功事件）
          authStore.setAuthData(
            response.data.user_info,
            response.data.access_token,
            'no-refresh-token' // 二维码登录没有刷新token
          )
        }

        // 调用成功回调
        props.onSuccess?.()
      } else if (response.data.status === 'expired') {
        // 二维码已过期
        status.value = 'expired'
        stopPolling()
        stopCountdown()
      }
    } catch (err) {
      console.error('检查登录状态失败:', err)
      // 不停止轮询，继续尝试
    } finally {
      pollingInProgress = false
      if (status.value === 'confirmed' || status.value === 'expired') {
        stopPolling()
      }
    }
  }, 2000)
}

// 停止轮询
const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval)
    pollingInterval = null
  }
  pollingInProgress = false
}

// 开始倒计时
const startCountdown = () => {
  // 清除之前的倒计时
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }

  countdownInterval = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      status.value = 'expired'
      stopPolling()
      stopCountdown()
    }
  }, 1000)
}

// 停止倒计时
const stopCountdown = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
}

// 组件挂载时生成二维码
onMounted(() => {
  void generateQRCode()
})

// 组件卸载时清理
onUnmounted(() => {
  stopPolling()
  stopCountdown()
})
</script>

<style scoped>
.qr-login-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.qr-loading,
.qr-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  color: #ccc;
}

.qr-error {
  text-align: center;
}

.qr-error p {
  color: #e74c3c;
  margin: 0;
}

.retry-button {
  margin-top: 10px;
}

.qr-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  width: 100%;
}

.qr-code-wrapper {
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  padding: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.qr-code-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-expired-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.expired-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  color: #fff;
}

.expired-content p {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.refresh-button {
  background: var(--p-primary-400);
  border: none;
  padding: 10px 20px;
}

.qr-scanned-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(39, 174, 96, 0.15);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.scanned-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #fff;
  background: rgba(39, 174, 96, 0.9);
  padding: 20px 30px;
  border-radius: 12px;
}

.scanned-content i {
  color: #fff !important;
}

.scanned-content p {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.qr-instructions {
  text-align: center;
  color: #ccc;
}

.qr-instructions ol {
  list-style-position: inside;
  padding: 0;
  margin: 0;
  text-align: left;
  display: inline-block;
}

.qr-instructions li {
  padding: 8px 0;
  font-size: 14px;
}

/* 用户预览信息 */
.user-preview {
  padding: 1.5rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 1.5rem;
  width: 100%;
  max-width: 350px;
}

.user-preview-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.user-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ff6b35;
}

.user-avatar-placeholder {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.user-info {
  flex: 1;
  text-align: left;
}

.user-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.25rem;
}

.user-phone {
  font-size: 0.9rem;
  color: #aaa;
}

.waiting-confirm {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  background-color: rgba(255, 107, 53, 0.1);
  border-radius: 8px;
  color: #ff6b35;
  font-size: 0.95rem;
  border: 1px solid rgba(255, 107, 53, 0.2);
}

.waiting-confirm i {
  font-size: 1rem;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .qr-code-wrapper {
    width: 180px;
    height: 180px;
    padding: 12px;
  }

  .qr-instructions li {
    font-size: 13px;
  }

  .user-avatar,
  .user-avatar-placeholder {
    width: 50px;
    height: 50px;
  }

  .user-name {
    font-size: 1rem;
  }

  .user-phone {
    font-size: 0.85rem;
  }
}
</style>
